import * as React from 'react';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { splitTextByLinks } from '../../utils/LinkUtils';
import { UserName } from '../name_component/user/UserNameComponent';

interface MentionNameProps {
	element: any;
	isOwnMessage: boolean;
}

// An @mention renders as a highlighted, hoverable name. The highlight sits on the message bubble, so
// it needs a different backdrop on the sender's own (darker) bubble.
const MentionName: React.FC<MentionNameProps> = ({ element, isOwnMessage }) => {
	const renderName = () => (
		<span className={`${isOwnMessage ? 'bg-[#0069cc]' : 'bg-[#c7cfd6]'} cursor-pointer`}>
			{nullSafeGetOrElse('userObject.nameGiven', element, nullSafeGet('user.nameGiven', element))}{' '}
			{nullSafeGetOrElse('userObject.nameFamily', element, nullSafeGet('user.nameFamily', element))}
		</span>
	);

	return <UserName contact={element.userObject || element.user} renderName={renderName} />;
};

const isResolvedMention = (element: any): boolean =>
	element.type === 'user' &&
	!!(nullSafeGet('userObject.nameGiven', element) || nullSafeGet('user.nameGiven', element));

// A link inside a bubble opens in a new tab and must not bubble its click up to the bubble itself.
const stopClickPropagation = (event: React.MouseEvent) => event.stopPropagation();

// The outgoing bubble is solid blue, so a link only reads on it in white; everywhere else it takes
// the same purple as the transcription toggle beside it.
const OWN_MESSAGE_LINK_CLASS_NAME = 'break-words underline text-white hover:text-white';
const LINK_CLASS_NAME = 'break-words underline text-purple-7 hover:text-purple-7';

interface LinkifiedTextProps {
	text: string;
	isOwnMessage: boolean;
}

/**
 * Note text with any URLs in it turned into links. The same segmentation feeds the Links list in
 * the notes media view, so a link that shows up there is clickable in the conversation too.
 */
const LinkifiedText: React.FC<LinkifiedTextProps> = ({ text, isOwnMessage }) => (
	<>
		{splitTextByLinks(text).map((segment, index) =>
			segment.href ? (
				<a
					key={index}
					className={isOwnMessage ? OWN_MESSAGE_LINK_CLASS_NAME : LINK_CLASS_NAME}
					href={segment.href}
					target="_blank"
					rel="noreferrer"
					onClick={stopClickPropagation}
				>
					{segment.value}
				</a>
			) : (
				<React.Fragment key={index}>{segment.value}</React.Fragment>
			)
		)}
	</>
);

interface MessageContentProps {
	note: any;
	/** The sender's own messages render mentions on the outgoing bubble's darker background. */
	isOwnMessage?: boolean;
}

/**
 * A note's body: either plain text, or a list of elements where `user` entries are @mentions. Shared
 * by the chat bubble and the audio transcription so a mention reads the same wherever it surfaces.
 */
export const MessageContent: React.FC<MessageContentProps> = ({ note, isOwnMessage = false }) => {
	if (!note.elements) {
		return <LinkifiedText text={note.text} isOwnMessage={isOwnMessage} />;
	}

	return (
		<>
			{note.elements.map((element, index) =>
				isResolvedMention(element) ? (
					<MentionName key={index} element={element} isOwnMessage={isOwnMessage} />
				) : element.type === 'user' ? (
					// An unresolved mention has no name to show, so its raw value renders as-is — never
					// as a link, since that value is a user id rather than something the author typed.
					<span key={index}>{element.value}</span>
				) : (
					<LinkifiedText key={index} text={element.value} isOwnMessage={isOwnMessage} />
				)
			)}
		</>
	);
};
